import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FormattedMessage, FormattedDate } from 'react-intl';
import format from 'date-fns/format';

import AvailabilitySlots from '../AvailabilitySlots';
import AvailabilityHSDetails from '../AvailabilityHSDetails';

import VisitRequest from '../VisitRequest';
import Table from '../../components/Table';
import Visibility from '../../components/Visibility';

import { createAvailabilityBlocks, arrangeAppointmentsByDay, isBlocked, isOpen, parseISODate, NO_AVAILABILITY } from './utils';
import './styles.css';

const TableHeader = ({ week }) => (
  <Table.Header>
    <Table.Row styleName="week-header-row">
      <Table.HeaderCell styleName="week-header" width={5} />
      <Table.HeaderCell styleName="week-header" width={2}>
        MON<br /><FormattedDate value={new Date(week[0])} month="short" day="numeric" timeZone="UTC" />
      </Table.HeaderCell>
      <Table.HeaderCell styleName="week-header" width={2}>
        TUES<br /><FormattedDate value={new Date(week[1])} month="short" day="numeric" timeZone="UTC" />
      </Table.HeaderCell>
      <Table.HeaderCell styleName="week-header" width={2}>
        WED<br /><FormattedDate value={new Date(week[2])} month="short" day="numeric" timeZone="UTC" />
      </Table.HeaderCell>
      <Table.HeaderCell styleName="week-header" width={2}>
        THUR<br /><FormattedDate value={new Date(week[3])} month="short" day="numeric" timeZone="UTC" />
      </Table.HeaderCell>
      <Table.HeaderCell styleName="week-header" width={2}>
        FRI<br /><FormattedDate value={new Date(week[4])} month="short" day="numeric" timeZone="UTC" />
      </Table.HeaderCell>
    </Table.Row>
  </Table.Header>
);
TableHeader.propTypes = {
  week: PropTypes.array.isRequired,
};

class VisitsAvailability extends React.PureComponent {
  static propTypes = {
    week: PropTypes.arrayOf(String),
    viewer: PropTypes.object,
    today: PropTypes.string,
    scrollContextRef: PropTypes.object,
  };

  state = { open: false, dayId: '', appointmentId: '', webInstructions: '', headerOverlay: false };

  close = () => {
    this.setState({ open: false });
  };

  open = (dayId, appointmentId, webInstructions) => {
    this.setState({ open: true, dayId, appointmentId, webInstructions });
  };

  showHeaderOverlay = () => {
    this.setState({ headerOverlay: true });
  };

  hideHeaderOverlay = () => {
    this.setState({ headerOverlay: false });
  };

  render() {
    const { week, today, scrollContextRef, viewer } = this.props;
    const { dayId, appointmentId, open, headerOverlay } = this.state;

    const isForProfitInstitution = get(viewer, 'institution.field_he_control') === 'PRIVATE_FORPROFIT';
    const sortedRepVisitHighSchoolsWithinProximity = viewer.repVisitHighSchoolsWithinProximity.length ? [
      viewer.repVisitHighSchoolsWithinProximity[0],
      ...viewer.repVisitHighSchoolsWithinProximity.slice(1).sort((school1, school2) => (
        parseFloat(school1.distance.text) > parseFloat(school2.distance.text) ? 1 : -1
      )),
    ] : [];

    return (
      <div>
        {dayId && (
        <VisitRequest
          id={dayId}
          appointmentId={appointmentId}
          webInstructions={this.state.webInstructions}
          open={open}
          onClose={this.close}
        />
        )}
        <Visibility
          context={scrollContextRef}
          onBottomPassed={this.showHeaderOverlay}
          onBottomVisible={this.hideHeaderOverlay}
          once={false}
          offset={scrollContextRef ? scrollContextRef.getBoundingClientRect().top : 0}
          styleName="visibility-container"
        >
          {headerOverlay && (
          <div styleName="header-overlay">
            <Table celled padded aria-hidden="true" styleName="table header-table">
              <TableHeader week={week} />
            </Table>
          </div>
          )}
        </Visibility>
        <Table celled padded styleName="table" data-cy="repvisit-schedule-visit-table">
          <caption styleName="hidden">Visits Availability</caption>
          <TableHeader week={week} />
          <Table.Body>
            {sortedRepVisitHighSchoolsWithinProximity.map((distanceWrapper) => {
              const row = get(distanceWrapper, 'toSchool');
              const rsvpDeadline = get(row, 'repVisitsAccount.availabilitySettings.rsvpDeadline');
              const allowForProfit = get(row, 'repVisitsAccount.allowForProfit');
              const webInstructions = get(row, 'repVisitsAccount.webInstructions', '');
              let availabilityVisible = get(row, 'repVisitsAccount.availabilityVisible');

              if (isForProfitInstitution && allowForProfit === false) {
                availabilityVisible = false;
              }
              const firstVisit = get(row, 'repVisitsAccount.firstVisitDay');
              const lastVisit = get(row, 'repVisitsAccount.lastVisitDay');
              const repVisitsNextAppointmentDate = get(row, 'repVisitsNextAppointmentDate');
              const availability = arrangeAppointmentsByDay(week, row.repVisitsAvailability);
              const cells = createAvailabilityBlocks(availability, today, rsvpDeadline, firstVisit, lastVisit, availabilityVisible);
              return (
                <Table.Row key={row.id}>
                  <Table.Cell>
                    <AvailabilityHSDetails distanceWrapper={distanceWrapper} />
                  </Table.Cell>
                  {cells.map((cell, i) => {
                    const cellKey = `${row.id}:${i}`;
                    const props = {
                      key: cellKey,
                      textAlign: 'center',
                      colSpan: cell.colSpan,
                    };
                    if (isForProfitInstitution && allowForProfit === false) {
                      return (
                        <Table.Cell
                          styleName="no-allow-for-profit"
                          width={10}
                          {...props}
                        >
                          <FormattedMessage
                            id="visits-do-not-allowForProfit"
                            defaultMessage="This high school only allows non-profit institutions to request visits."
                          />
                        </Table.Cell>
                      );
                    } else if (!availabilityVisible) {
                      return (
                        <Table.Cell {...props} styleName="no-availability">
                          <FormattedMessage
                            id="visits-availability.notPublished"
                            defaultMessage="This high school has not published a schedule for visits."
                          />
                        </Table.Cell>
                      );
                    } else if (isBlocked(cell.status)) {
                      const reason = cell.status.replace('BLOCKED_', '').replace(/_/g, ' ').toLowerCase();
                      return (
                        <Table.Cell {...props} styleName="blocked" data-cy="repvisit-schedule-visit-blocked">
                          <span styleName="blocked-header">
                            <FormattedMessage
                              id="visits-availability.blocked"
                              defaultMessage="BLOCKED"
                            />
                          </span>
                          <p styleName="p-with-no-bottom-margin">{reason}</p>
                          {props.colSpan === 5 && repVisitsNextAppointmentDate &&
                          (
                            <FormattedMessage
                              id="visits-availability.nextAvailability"
                              defaultMessage="Next Available Visit is {nextDate}"
                              values={{ nextDate: format(parseISODate(repVisitsNextAppointmentDate.date), 'MMM dd, yyyy') }}
                            />
                          )}
                          {props.colSpan === 5 && !repVisitsNextAppointmentDate &&
                          (
                            <FormattedMessage
                              id="visits-availability.noFutureAvailability"
                              defaultMessage="No Future Availability At This Time"
                            />
                          )}
                        </Table.Cell>
                      );
                    } else if (cell.status === NO_AVAILABILITY) {
                      return (
                        <Table.Cell {...props} styleName="no-availability">
                          <FormattedMessage
                            id="visits-availability.noAvailability"
                            defaultMessage="No Appointments Available"
                          />
                          {props.colSpan === 5 && repVisitsNextAppointmentDate ?
                            (
                              <span>
                                <br />
                                <FormattedMessage
                                  id="visits-availability.nextAvailability"
                                  defaultMessage="Next Available Visit is {nextDate}"
                                  values={{ nextDate: format(parseISODate(repVisitsNextAppointmentDate.date), 'MMM dd, yyyy') }}
                                />
                              </span>
                            ) :
                            ''}
                        </Table.Cell>
                      );
                    } else if (isOpen(cell.status)) {
                      props.verticalAlign = 'top';
                      return (
                        <Table.Cell {...props}>
                          <AvailabilitySlots
                            fromDate={cell.date}
                            toDate={cell.date}
                            dayId={cell.id}
                            onClick={this.open}
                            appointments={cell.appointments}
                            webInstructions={webInstructions}
                          />
                        </Table.Cell>
                      );
                    } else {
                      return <Table.Cell {...props} />;
                    }
                  })}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    );
  }
}

export default VisitsAvailability;
